'use client'

import { isArray, isNil } from 'lodash-es'
import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

const DEFAULT_THUMB_COUNT = 2

export type SliderValueFormat = {
  /** Display를 위한 값으로 변환 (예: "1000000" → "100+") */
  formatValue: (value: number) => string
  /** Display된 값을 실제값으로 변환 (예: "100+" → 1000000) */
  parse: (input: string) => number
  maxLength: number
  unit: ReactNode
  /** 최소>최대 또는 최대<최소로 역전됐을 때 blur 시 반대쪽을 이 간격만큼 벌릴 때 사용 (예: 1만 원 = 10000) */
  gapWhenReversed?: number
}

export type SliderState = {
  selectedRange: number[]
  updateInternalSelectedRange: (value: number[]) => void
  updateSelectedRange: (value: number[]) => void
  range: { min: number; max: number }
  thumbCount: number
  step?: number
  minStepsBetweenThumbs?: number
  valueFormat: SliderValueFormat
}

const SliderContext = createContext<SliderState | null>(null)

export const useSliderContext = () => {
  const context = useContext(SliderContext)

  if (!context) {
    throw new Error('useSliderContext must be used within Slider.Root')
  }

  return context
}

export { SliderContext }

type SliderProviderProps = {
  value?: number[]
  defaultValue?: number[]
  onValueChange?: (value: number[]) => void
  min?: number
  max?: number
  step?: number
  minStepsBetweenThumbs?: number
  valueFormat: SliderValueFormat
}

export const SliderProvider = ({
  children,
  value: valueProp,
  defaultValue: defaultValueProp,
  onValueChange,
  min = 0,
  max = 0,
  step,
  minStepsBetweenThumbs,
  valueFormat,
}: PropsWithChildren<SliderProviderProps>) => {
  const [selectedRange, setSelectedRange] = useState(() => {
    if (!isNil(defaultValueProp)) {
      return defaultValueProp
    }

    if (!isNil(valueProp)) {
      return valueProp
    }

    return [min, max]
  })

  const thumbCount = (() => {
    if (isArray(defaultValueProp)) return defaultValueProp.length
    if (isArray(valueProp)) return valueProp.length

    return DEFAULT_THUMB_COUNT
  })()

  const range = { min, max }

  const updateInternalSelectedRange = useCallback(
    (range: number[]) => {
      setSelectedRange(range)
    },
    [setSelectedRange],
  )

  const updateSelectedRange = useCallback(
    (range: number[]) => {
      setSelectedRange(range)
      onValueChange?.(range)
    },
    [onValueChange, setSelectedRange],
  )

  useEffect(() => {
    if (isNil(valueProp)) return
    setSelectedRange(valueProp)
  }, [valueProp])

  const value: SliderState = {
    selectedRange,
    updateInternalSelectedRange,
    updateSelectedRange,
    range,
    thumbCount,
    step,
    minStepsBetweenThumbs,
    valueFormat,
  }

  return (
    <SliderContext.Provider value={value}>{children}</SliderContext.Provider>
  )
}
